import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { EventDto, MarketDto, SelectionDto } from '~/components/sportsbook/lobby/api/sbapi/sbapi-api-model';
import { SmallBold, Small } from '@comeon/ui';
import { Link } from '~/utils/navigation';
import useSportsbookRoutes from '~/components/sportsbook/lobby/hooks/use-sportsbook-routes';
import {
	BetRecommendationsBulletConnectedList,
	BetRecommendationsBulletConnectedListItem
} from './sportsbook-bet-recommendations-bullet-connected-list';
import SportsbookLivePill from '~/components/sportsbook/lobby/components/sportsbook-live-pill';
import SportsbookTextKeys from '~/components/sportsbook/lobby/sportsbook-text-keys';
import { Translation } from '~/components/translation/types';

const BetItemWrapper = styled.div<{ $shouldDeepLink: boolean }>`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 2px;
	align-items: stretch;

	${({ $shouldDeepLink }) =>
		$shouldDeepLink &&
		css`
			cursor: pointer;
			@media (hover: hover) and (pointer: fine) {
				&:hover {
					color: var(--main-text-primary);
					border-color: var(--main-text-primary);
				}
			}
		`}
`;

const ItemTextRow = styled.div`
	line-height: 1;
	width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: inherit;
`;

const ItemTextWrapper = styled.div`
	display: flex;
	gap: 3px;
	line-height: 1;
	width: 100%;
	color: inherit;
`;

const ItemText = styled(Small)`
	display: inline;
	color: inherit;
`;

const ItemTextStrong = styled(SmallBold)`
	display: inline;
	color: inherit;
`;

const useLinkToEventPage = (event?: EventDto) => {
	const sportsbookRoutes = useSportsbookRoutes();

	return useMemo(
		() =>
			event &&
			sportsbookRoutes.getEventPageLink(
				event.sportId,
				event.sportName,
				event.leagueId,
				event.leagueName,
				event.id,
				event.eventName,
				undefined,
				true
			),
		[event, sportsbookRoutes]
	);
};

type BetRecommendationsBetItemProps = {
	event: EventDto;
	market: MarketDto;
	selection: SelectionDto;
	translations: Translation;
	shouldDisplayEventName: boolean;
	shouldDeepLink: boolean;
};

const BetRecommendationsBetItem: React.FC<BetRecommendationsBetItemProps> = ({
	event,
	market,
	selection,
	translations,
	shouldDisplayEventName,
	shouldDeepLink
}) => {
	const isPreCanned = useMemo(() => {
		return market?.marketType.isPreCanned;
	}, [market?.marketType]);

	const preCannedLegs = useMemo(() => {
		return selection?.name.split(/\s?[,&|]\s?/);
	}, [selection?.name]);

	const eventLink = useLinkToEventPage(event) || '';

	if (isPreCanned) {
		return (
			<BetItemWrapper
				href={shouldDeepLink ? eventLink : undefined}
				as={shouldDeepLink ? Link : 'div'}
				$shouldDeepLink={shouldDeepLink}
			>
				{shouldDisplayEventName && (
					<ItemTextRow>
						<ItemTextStrong>{event.eventName}</ItemTextStrong>
					</ItemTextRow>
				)}
				<BetRecommendationsBulletConnectedList gap="2px">
					{preCannedLegs?.map((legName: string, index: number) => (
						<BetRecommendationsBulletConnectedListItem
							// eslint-disable-next-line react/no-array-index-key
							key={`precanned-leg-${index}`}
							isLast={index === preCannedLegs.length - 1}
						>
							<ItemTextRow>
								<ItemText>{legName}</ItemText>
							</ItemTextRow>
						</BetRecommendationsBulletConnectedListItem>
					))}
				</BetRecommendationsBulletConnectedList>
			</BetItemWrapper>
		);
	}

	return (
		<BetItemWrapper
			href={shouldDeepLink ? eventLink : undefined}
			as={shouldDeepLink ? Link : 'div'}
			$shouldDeepLink={shouldDeepLink}
		>
			<ItemTextRow>
				<ItemTextStrong>{selection.name}</ItemTextStrong>
				<ItemText> | </ItemText>
				<ItemText>{market.name}</ItemText>
			</ItemTextRow>
			<ItemTextWrapper>
				<ItemTextRow>
					<ItemText>{event.eventName}</ItemText>
				</ItemTextRow>
				{event.isLive && <SportsbookLivePill>{translations[SportsbookTextKeys.LIVE]}!</SportsbookLivePill>}
			</ItemTextWrapper>
		</BetItemWrapper>
	);
};

export default BetRecommendationsBetItem;
